import { useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { useAuthStore } from '../contexts/AuthContext';
import { useChartDispatch, useChartStore } from '../contexts/ChartContext';
import { useDashboardChartsDispatch } from '../contexts/single-dashboard/DashboardChartsContext';

/**
 * Safely parses JSON response, providing descriptive error messages
 * @param {Response} response - The fetch response object
 * @returns {Promise<Object>} Parsed JSON data, guaranteed to be an object
 * @throws {Error} With status code and message if parsing fails
 */
async function parseJsonResponse(response) {
  try {
    const parsed = await response.json();
    // Return parsed data as-is if it's an object or array
    // Only convert primitives or null to empty object
    return typeof parsed === 'object' && parsed !== null
      ? parsed
      : {};
  } catch (parseError) {
    throw new Error(
      `Failed to parse response as JSON (${response.status} ${response.statusText})`
    );
  }
}

export default function useGetCharts() {
  const chartDispatch = useChartDispatch();
  const { selectedAccount } = useAuthStore();

  useEffect(() => {
    chartDispatch({ type: 'setIsLoadingCharts', payload: true });

    //  for super admins with "Innovare" selected as the account (no id), return template drafts
    let url = selectedAccount.id
      ? `/api/charts?account=${selectedAccount.id}`
      : `/api/charts/templates?isDraft=true`;
    fetch(url, { credentials: 'include' })
      .then(async (r) => {
        const data = await parseJsonResponse(r);
        if (!r.ok) {
          throw new Error(data?.message || `HTTP ${r.status}: ${r.statusText}`);
        }
        return data;
      })
      .then((data) => {
        chartDispatch({ type: 'setCharts', payload: data });
      })
      .catch((error) => {
        console.error(
          'Error fetching charts:',
          error.message || 'A network error occurred.'
        );
        captureException(error, { tags: { context: 'useGetCharts' } });
        return error;
      })
      .finally(() => {
        chartDispatch({ type: 'setIsLoadingCharts', payload: false });
      });
  }, [selectedAccount, chartDispatch]);
}

// payload: {accountId: , dashboardId: , ChartId: }
export function useDeleteChart() {
  const dashboardChartsDispatch = useDashboardChartsDispatch();

  async function deleteChart(payload) {
    try {
      let resp = await fetch(
        `/api/dashboards/${payload.dashboardId}/charts/${payload.ChartId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await parseJsonResponse(resp);

      if (resp.ok) {
        //update the dashboardChart in context
        dashboardChartsDispatch({
          type: 'deleteDashboardChart',
          payload: { dashboardChartId: payload['ChartId'] },
        });
        return data;
      } else {
        const error = new Error(data.message || 'Error deleting chart');
        console.error('Error deleting chart:', data);
        captureException(error, { tags: { context: 'deleteChart' } });
        return { message: resp.statusText, ...data };
      }
    } catch (error) {
      console.error('Network error deleting chart:', error);
      captureException(error, { tags: { context: 'deleteChart_network' } });
      return { message: 'A network error occurred while deleting the chart.' };
    }
  }
  return deleteChart;
}

// return a function that accepts a chart from the list and saves it
export function useEditChart(dashboardId) {
  const chartDispatch = useChartDispatch();
  const dashboardChartsDispatch = useDashboardChartsDispatch();
  return async (chart) => {
    if (!chart?.id) return { message: 'Invalid chart data provided.' };
    try {
      const resp = await sendChartUpdate(chart, dashboardId);
      const data = await parseJsonResponse(resp);

      if (!resp.ok) {
        const error = new Error(data.message || 'Error editing chart');
        console.error('Error editing chart:', data);
        captureException(error, { tags: { context: 'useEditChart' } });
        return { message: resp.statusText, ...data };
      }
      if (!data?.Chart) {
        const error = new Error(
          'Invalid data returned from server after editing chart.'
        );
        console.error('Invalid data returned from server after editing chart.');
        captureException(error, {
          tags: { context: 'useEditChart_invalidData' },
        });
        return { message: 'Invalid data returned from server.' };
      }

      //add the chart to the chart list
      chartDispatch({ type: 'addChartToList', payload: data.Chart });

      //update the dashboardChart in context
      dashboardChartsDispatch({
        type: 'updateDashboardCharts',
        payload: { DashboardCharts: [data] },
      });

      return data.Chart;
    } catch (error) {
      console.error('Network error editing chart:', error);
      captureException(error, { tags: { context: 'useEditChart_network' } });
      return { message: 'A network error occurred while editing the chart.' };
    }
  };
}

export async function sendChartUpdate(chart, dashboardId) {
  return await fetch(`/api/dashboards/${dashboardId}/charts/${chart.id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(chart),
  });
}

export function useUpdateChart() {
  // this flickers on and off... may need useState in future to persist
  const chartDispatch = useChartDispatch();

  async function updateChart(chart) {
    chartDispatch({ type: 'setIsSavingChart', payload: true });
    try {
      let resp = await sendChartUpdate(chart, chart.DashboardId); // Assuming chart.DashboardId exists
      const updatedChart = await parseJsonResponse(resp);

      if (!resp.ok) {
        const error = new Error(updatedChart.message || 'Error updating chart');
        console.error('Error updating chart:', updatedChart);
        captureException(error, { tags: { context: 'useUpdateChart' } });
        return { message: resp.statusText, ...updatedChart };
      }
      if (!updatedChart)
        return { message: 'Invalid data returned from server.' };

      // the chart returned here contains more information than the chart list needs, in the future we may want to simplify somehow
      chartDispatch({ type: 'updateChartInList', payload: updatedChart });
      return updatedChart;
    } catch (error) {
      console.error('Network error updating chart:', error);
      captureException(error, { tags: { context: 'useUpdateChart_network' } });
      return { message: 'A network error occurred while updating the chart.' };
    } finally {
      chartDispatch({ type: 'setIsSavingChart', payload: false });
    }
  }
  return updateChart;
}

// useChartPublishing returns an object with two functions: one for publishing a chart and one for unpublishing
export function useChartPublishing() {
  const { selectedChart } = useChartStore();
  const chartDispatch = useChartDispatch();

  async function updatePublishStatus(status) {
    const updatedChart = { ...selectedChart, isPublishedToMarketplace: status };

    // update the chart in context
    chartDispatch({ type: 'setSelectedChart', payload: updatedChart });

    chartDispatch({ type: 'setIsLoadingTemplates', payload: true });
    chartDispatch({ type: 'setIsSavingChart', payload: true });
    chartDispatch({ type: 'setIsLoadingCharts', payload: true });

    try {
      const resp = await sendChartUpdate(
        updatedChart,
        updatedChart.DashboardId
      ); // Assuming updatedChart.DashboardId exists

      const data = await parseJsonResponse(resp);

      if (!resp.ok) {
        const error = new Error(
          data.message || 'Error updating publish status'
        );
        console.error('Error updating publish status:', data);
        captureException(error, { tags: { context: 'updatePublishStatus' } });
        return { message: resp.statusText, ...data };
      }
      return data;
    } catch (error) {
      console.error('Network error updating publish status:', error);
      captureException(error, {
        tags: { context: 'updatePublishStatus_network' },
      });
      return { message: 'A network error occurred.' };
    } finally {
      chartDispatch({ type: 'setIsSavingChart', payload: false });
    }
  }

  async function publishChart() {
    if (window.confirm('Are you sure you want to publish?')) {
      const chart = await updatePublishStatus(true);
      if (chart?.id) {
        // remove the chart from the list
        chartDispatch({ type: 'removeChart', payload: chart });

        // add the chart to templates
        chartDispatch({ type: 'addTemplate', payload: chart });
      }

      chartDispatch({ type: 'setIsLoadingCharts', payload: false });
      chartDispatch({ type: 'setIsLoadingTemplates', payload: false });

      return chart;
    }
  }

  async function unpublishChart() {
    if (window.confirm('Are you sure you want to unpublish?')) {
      const chart = await updatePublishStatus(false);
      if (chart?.id) {
        // remove the chart from templates
        chartDispatch({ type: 'removeTemplate', payload: chart });

        // add the chart to the list
        chartDispatch({ type: 'addChartToList', payload: chart });
      }

      chartDispatch({ type: 'setIsLoadingTemplates', payload: false });
      chartDispatch({ type: 'setIsLoadingCharts', payload: false });

      return chart;
    }
  }

  return {
    publishChart: publishChart,
    unpublishChart: unpublishChart,
  };
}

// payload: {imgUrl, accountId, chartId}
export function useUpdateChartScreenshot(payload) {
  const chartDispatch = useChartDispatch();
  const [chartObj, setChartObj] = useState(payload);

  async function updateChartScreenshot() {
    try {
      const res = await fetch(`/api/charts/${chartObj.id}/screenshot`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(chartObj),
      });

      const data = await res.json();

      if (res.ok) {
        chartDispatch({ type: 'updateChartScreenshot', payload: data });
        return data;
      } else {
        const error = new Error(
          data.message || 'Error updating chart screenshot'
        );
        console.error('Error updating chart screenshot:', data);
        captureException(error, { tags: { context: 'updateChartScreenshot' } });
        return { message: res.statusText, ...data };
      }
    } catch (error) {
      console.error('Network error updating chart screenshot:', error);
      captureException(error, {
        tags: { context: 'updateChartScreenshot_network' },
      });
      return { message: 'A network error occurred.' };
    }
  }
  return { chartObj, setChartObj, updateChartScreenshot };
}
